import { T, useTolgee } from '@tolgee/react';
import { Alert } from 'antd';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { NewRelic } from '../../../hooks/useNewRelic';
import {
  MaterialAttachment,
  useUploadMaterialAttachment,
} from '../../../hooks/useUploadMaterialAttachment';
import { createFileFromText } from '../../../utils/fileUtils';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import BaseModal from '../../_molecules/BaseModal';

export function UseMyTextModal({
  isVisible,
  onCancel,
  onFileUploadSuccess,
  tenantId,
}: {
  isVisible: boolean;
  onCancel: () => void;
  onFileUploadSuccess: (file: MaterialAttachment) => void;
  tenantId: string;
}) {
  const { t } = useTolgee();
  const metrics = useMetrics();
  const [error, setError] = useState<boolean>(false);
  const {
    upload: uploadGenericFile,
    reset: resetFileUpload,
    isUploading,
  } = useUploadMaterialAttachment(tenantId);

  const [modalTextValue, setModalTextValue] = useState<string>('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const onCancelModal = () => {
    setModalTextValue('');
    resetFileUpload();
    onCancel();
  };

  return (
    <BaseModal visible={isVisible} hideCancel onCancel={onCancelModal} width="600px">
      <h1 style={{ marginBottom: 0 }}>
        <T keyName="Paste in Text" defaultValue="Paste in Text" />
      </h1>
      <PasteModalContent>
        <TextArea
          ref={textAreaRef}
          value={modalTextValue}
          autoFocus
          onChange={e => setModalTextValue(e.target.value)}
          placeholder={t(
            'Paste text here to shape your lesson',
            'Paste text here to shape your lesson',
          )}
          autoSize={{ minRows: 8, maxRows: 15 }}
          style={{
            width: '100%',
            resize: 'vertical',
          }}
          textAreaStyled={{
            borderRadius: '10px',
            fontSize: 16,
            fontWeight: 600,
            boxShadow: theme.effects.floatingCreatorElementShadow,
          }}
          onClick={e => {
            e.stopPropagation();
          }}
          onPaste={e => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            const textArea = e.target as HTMLTextAreaElement;
            const start = textArea.selectionStart;
            const end = textArea.selectionEnd;
            const currentValue = textArea.value;
            const newValue =
              currentValue.substring(0, start) + text + currentValue.substring(end);

            // Update the state directly instead of setting textarea value
            setModalTextValue(newValue);
          }}
        />
        {!error ? null : (
          <Alert
            message={
              <T
                keyName="CreatePage.UseMyMaterials.TextModal.ErrorUploadingFile"
                defaultValue="Error uploading file"
              />
            }
            type="error"
          />
        )}
        <ModalButtons>
          <Button
            onClick={onCancelModal}
            styles={{
              backgroundColor: 'transparent',
              color: theme.colors.greyDark,
              border: `1px solid ${theme.colors.grey}`,
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            <T
              keyName="ObjectiveRefinement.UseMyMaterials.Text.Modal.CancelText"
              defaultValue="Cancel"
            />
          </Button>
          <Button
            disabled={!modalTextValue.trim()}
            loading={isUploading}
            onClick={() => {
              setError(false);
              const text = modalTextValue.trim();
              const file = createFileFromText(text);
              uploadGenericFile(
                { file, attachmentMethod: 'paste' },
                {
                  onSuccess: result => {
                    onFileUploadSuccess(result.materialAttachment);
                    metrics.logEvent(
                      'CreatePage.UseMyMaterials.TextModal.FileUploadSuccess',
                      {
                        fileSize: file.size,
                        fileName: file.name,
                        fileUploadId: result.materialAttachment.fileUploadId,
                        fileText: text.substring(0, 100),
                      },
                    );
                    onCancelModal();
                  },
                  onError: error => {
                    NewRelic()?.noticeError(error);
                    setError(true);
                  },
                },
              );
            }}
            styles={{
              backgroundColor: theme.colors.orange,
              color: theme.colors.white,
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            <T
              keyName="ObjectiveRefinement.UseMyMaterials.Text.Modal.ConfirmText"
              defaultValue="Use My Text"
            />
          </Button>
        </ModalButtons>
      </PasteModalContent>
    </BaseModal>
  );
}

const PasteModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0px;
  position: relative;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
